import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type {
  GroupDetailDTO,
  GroupInviteDTO,
  GroupInvitePolicy,
  GroupMemberDTO,
  GroupMemberProfileDTO,
} from '@nexply/api-client';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Label,
  PageHeader,
  SegmentedControl,
  Sheet,
  Textarea,
  cn,
  useToast,
} from '@nexply/ui';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  Link2,
  LogOut,
  Mail,
  Pencil,
  Plus,
  QrCode,
  Share2,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { QRModal } from '../../components/QRModal.js';
import { InvitePolicyPicker, ROLE_LABEL, ROLE_TONE } from './groups.js';

type Tab = 'members' | 'invite' | 'settings';

export function GroupDetailPage() {
  const { groupId } = useParams({ strict: false }) as { groupId: string };
  const [tab, setTab] = useState<Tab>('members');
  const q = useQuery({ queryKey: ['groups', groupId], queryFn: () => api.groups.get(groupId) });
  const g = q.data?.group;

  if (q.isLoading) return <div className="h-40 rounded-2xl bg-raise animate-pulse max-w-4xl" />;
  if (q.isError || !g) {
    return (
      <EmptyState
        mascot="idle"
        title="Gruppe nicht gefunden"
        description="Entweder gibt es die Gruppe nicht mehr oder du bist kein Mitglied."
        action={
          <Link to="/groups">
            <Button variant="secondary">Zu meinen Gruppen</Button>
          </Link>
        }
      />
    );
  }

  const tabs: { value: Tab; label: string }[] = [
    { value: 'members', label: `Mitglieder (${g.memberCount})` },
    ...(g.canInvite ? [{ value: 'invite' as Tab, label: 'Einladen' }] : []),
    ...(g.myRole === 'OWNER' ? [{ value: 'settings' as Tab, label: 'Einstellungen' }] : []),
  ];

  return (
    <div className="space-y-5 max-w-4xl animate-slide-up">
      <Link to="/groups" className="inline-flex items-center gap-1.5 text-sm text-text-dim hover:text-text">
        <ArrowLeft className="h-4 w-4" />
        Alle Gruppen
      </Link>
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-2">
            Netzwerk-Gruppe <Badge tone={ROLE_TONE[g.myRole]}>{ROLE_LABEL[g.myRole]}</Badge>
          </span>
        }
        title={g.name}
        subtitle={g.description ?? undefined}
      />
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-dim">
        <span>Einladen dürfen:</span>
        <Badge tone={g.invitePolicy === 'ALL_MEMBERS' ? 'success' : 'warn'} size="md">
          {g.invitePolicy === 'ALL_MEMBERS' ? 'Alle Mitglieder' : 'Nur Admins'}
        </Badge>
        {g.myRole === 'OWNER' && (
          <button className="text-gold-light hover:underline" onClick={() => setTab('settings')}>
            ändern
          </button>
        )}
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
        <SegmentedControl size="sm" value={tab} onChange={setTab} options={tabs} />
      </div>

      {tab === 'members' && <MembersTab g={g} />}
      {tab === 'invite' && g.canInvite && <InviteTab g={g} />}
      {tab === 'settings' && g.myRole === 'OWNER' && <SettingsTab g={g} />}
    </div>
  );
}

function IconBtn({ title, onClick, children, danger }: { title: string; onClick: () => void; children: React.ReactNode; danger?: boolean }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-lg transition',
        danger ? 'text-danger hover:bg-danger/10' : 'text-text-muted hover:bg-raise hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Mitglieder
// ---------------------------------------------------------------------------

function MembersTab({ g }: { g: GroupDetailDTO }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const isOwner = g.myRole === 'OWNER';
  const [profileUserId, setProfileUserId] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['groups', g.id] });
    qc.invalidateQueries({ queryKey: ['groups'], exact: true });
  };

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.groups.removeMember(g.id, userId),
    onSuccess: () => {
      refresh();
      toast.success('Mitglied entfernt');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'ADMIN' | 'MEMBER' }) =>
      api.groups.setRole(g.id, userId, { role }),
    onSuccess: (_r, v) => {
      refresh();
      toast.success(v.role === 'ADMIN' ? 'Ist jetzt Admin' : 'Admin-Rechte entzogen');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const leave = useMutation({
    mutationFn: () => api.groups.leave(g.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Du bist ausgetreten');
      nav({ to: '/groups' });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canRemove = (m: GroupMemberDTO) =>
    m.userId !== user?.id && m.role !== 'OWNER' && (isOwner || (g.canManage && m.role === 'MEMBER'));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {g.members.map((m) => (
          <div key={m.userId} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-3">
            <button
              type="button"
              onClick={() => setProfileUserId(m.userId)}
              title="Profil ansehen"
              className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left transition hover:bg-raise/60 -m-1 p-1"
            >
              <Avatar name={m.name} src={m.photoUrl ?? undefined} size="md" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-text">
                    {m.name}
                    {m.userId === user?.id && <span className="text-text-muted"> (du)</span>}
                  </span>
                  <Badge tone={ROLE_TONE[m.role]}>{ROLE_LABEL[m.role]}</Badge>
                </div>
                <div className="truncate text-[11px] text-text-muted">
                  {m.email} · seit {new Date(m.joinedAt).toLocaleDateString('de-DE')}
                  {m.invitedByName && ` · eingeladen von ${m.invitedByName}`}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" />
            </button>
            <div className="flex shrink-0 gap-1">
              {isOwner && m.role === 'MEMBER' && (
                <IconBtn title="Zum Admin machen" onClick={() => setRole.mutate({ userId: m.userId, role: 'ADMIN' })}>
                  <ShieldCheck className="h-4 w-4" />
                </IconBtn>
              )}
              {isOwner && m.role === 'ADMIN' && (
                <IconBtn title="Admin-Rechte entziehen" onClick={() => setRole.mutate({ userId: m.userId, role: 'MEMBER' })}>
                  <ShieldOff className="h-4 w-4" />
                </IconBtn>
              )}
              {canRemove(m) && (
                <IconBtn
                  title="Aus der Gruppe entfernen"
                  danger
                  onClick={() => {
                    if (confirm(`${m.name} aus „${g.name}" entfernen?`)) removeMember.mutate(m.userId);
                  }}
                >
                  <UserMinus className="h-4 w-4" />
                </IconBtn>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-text-muted">
        {g.invitePolicy === 'ALL_MEMBERS'
          ? 'In dieser Gruppe darf jedes Mitglied Leute einladen.'
          : 'In dieser Gruppe dürfen nur Admins Leute einladen.'}{' '}
        Wie in einer WhatsApp-Gruppe: Wer dazukommt, ist automatisch mit allen hier vernetzt und steht in eurem Netzwerk
        als „Vernetzt durch Gruppe“. Admins können Mitglieder entfernen; Admin-Rechte vergibt der Ersteller.
      </p>

      {!isOwner && (
        <Button
          variant="ghost"
          size="sm"
          loading={leave.isPending}
          onClick={() => {
            if (confirm(`Aus „${g.name}" austreten? Du siehst die gemeinsamen Kontakte danach nicht mehr.`)) leave.mutate();
          }}
        >
          <LogOut className="h-4 w-4" />
          Gruppe verlassen
        </Button>
      )}

      <MemberProfileSheet g={g} userId={profileUserId} onClose={() => setProfileUserId(null)} />
    </div>
  );
}

/**
 * Profil eines Mitglieds: zeigt, was die Person in ihrem eigenen Nexply-Konto
 * über sich angegeben hat (Firma, Position, Kontaktwege, Pitch, Angebot, Suche).
 * Private Angaben wie Notizen, Tags oder Geburtstag bleiben außen vor.
 */
function MemberProfileSheet({ g, userId, onClose }: { g: GroupDetailDTO; userId: string | null; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const isMe = !!userId && userId === user?.id;

  const q = useQuery({
    queryKey: ['groups', g.id, 'member-profile', userId],
    queryFn: () => api.groups.memberProfile(g.id, userId!),
    enabled: !!userId,
  });
  const p: GroupMemberProfileDTO | undefined = q.data?.profile;

  const connect = useMutation({
    mutationFn: () => api.groups.connectMemberProfile(g.id, userId!),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: ['groups', g.id, 'member-profile', userId] });
      toast.success(r.alreadyExists ? 'Ihr seid bereits vernetzt' : 'Ihr seid jetzt vernetzt');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows: Array<[string, string | null, string | null]> = p
    ? [
        ['Firma', p.company, null],
        ['Position', p.role, null],
        ['Branche', p.industry, null],
        ['E-Mail', p.email, p.email ? `mailto:${p.email}` : null],
        ['Telefon', p.phone, p.phone ? `tel:${p.phone}` : null],
        ['Website', p.website, p.website ? externalHref(p.website) : null],
        ['LinkedIn', p.linkedin, p.linkedin ? externalHref(p.linkedin, 'https://www.linkedin.com/in/') : null],
        ['Instagram', p.instagram, p.instagram ? externalHref(p.instagram, 'https://www.instagram.com/') : null],
        ['XING', p.xing, p.xing ? externalHref(p.xing, 'https://www.xing.com/profile/') : null],
        ['Ort', [p.city, p.country].filter(Boolean).join(', ') || null, null],
      ]
    : [];
  const filled = rows.filter(([, v]) => v);

  return (
    <Sheet open={!!userId} onClose={onClose} title={p?.name ?? 'Profil'} description={`Mitglied in „${g.name}“`} size="lg">
      {q.isLoading && <div className="py-6 text-center text-sm text-text-muted">Lädt…</div>}
      {q.isError && <div className="py-6 text-center text-sm text-danger">{(q.error as Error).message}</div>}
      {p && (
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <Avatar name={p.name} src={p.photoUrl ?? undefined} size="xl" className="shrink-0" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone={ROLE_TONE[p.groupRole]}>{ROLE_LABEL[p.groupRole]}</Badge>
                {p.industry && <Badge tone="gold">{p.industry}</Badge>}
                {p.inMyNetworkMemberId && <Badge tone="success">Vernetzt</Badge>}
              </div>
              {(p.role || p.company) && (
                <div className="text-sm text-text-dim">{[p.role, p.company].filter(Boolean).join(' · ')}</div>
              )}
              <div className="text-[11px] text-text-muted">
                Dabei seit {new Date(p.joinedAt).toLocaleDateString('de-DE')}
              </div>
            </div>
          </div>

          {!p.hasProfile && (
            <div className="rounded-xl border border-dashed border-line p-3 text-sm text-text-muted">
              {isMe
                ? 'Du hast dein Profil noch nicht ausgefüllt. Andere Mitglieder sehen hier nur deinen Namen und deine E-Mail.'
                : 'Diese Person hat ihr Profil noch nicht ausgefüllt. Sichtbar sind nur Name und E-Mail aus dem Konto.'}
            </div>
          )}

          {filled.length > 0 && (
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {filled.map(([k, v, href]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-line pb-1">
                  <span className="shrink-0 text-text-muted">{k}</span>
                  {href ? (
                    <a
                      href={href}
                      target={href.startsWith('http') ? '_blank' : undefined}
                      rel="noreferrer"
                      className="truncate text-right text-text-dim hover:text-gold-light"
                      title={v ?? ''}
                    >
                      {v}
                    </a>
                  ) : (
                    <span className="truncate text-right text-text-dim" title={v ?? ''}>
                      {v}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {p.pitch && <ProfileBlock label="Pitch" text={p.pitch} />}
          {p.offering && <ProfileBlock label="Bietet an" text={p.offering} />}
          {p.seeking && <ProfileBlock label="Sucht" text={p.seeking} />}

          <div className="flex flex-col-reverse gap-2 border-t border-line pt-3 sm:flex-row sm:justify-end">
            {isMe ? (
              <Button variant="ghost" onClick={() => nav({ to: '/profile' })}>
                <Pencil className="h-4 w-4" />
                Mein Profil bearbeiten
              </Button>
            ) : p.inMyNetworkMemberId ? (
              <Button variant="ghost" onClick={() => nav({ to: '/members', search: { id: p.inMyNetworkMemberId ?? undefined } })}>
                <Users className="h-4 w-4" />
                In meinem Netzwerk öffnen
              </Button>
            ) : (
              <Button loading={connect.isPending} onClick={() => connect.mutate()}>
                <UserPlus className="h-4 w-4" />
                Jetzt vernetzen
              </Button>
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}

function ProfileBlock({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="whitespace-pre-wrap text-sm text-text-dim">{text}</div>
    </div>
  );
}

/** Aus einer frei eingetippten Angabe (URL oder nur Handle) einen klickbaren Link bauen. */
function externalHref(value: string, handleBase?: string): string {
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) return v;
  if (handleBase && !v.includes('/') && !v.includes('.')) return handleBase + v.replace(/^@/, '');
  return `https://${v.replace(/^\/+/, '')}`;
}

// ---------------------------------------------------------------------------
// Einladen: manuell per E-Mail + Einladungslinks
// ---------------------------------------------------------------------------

type UsageMode = 'unlimited' | 'limited';

function InviteTab({ g }: { g: GroupDetailDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [usage, setUsage] = useState<UsageMode>('unlimited');
  const [maxUses, setMaxUses] = useState('10');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [showRevoked, setShowRevoked] = useState(false);

  const invites = useQuery({ queryKey: ['groups', g.id, 'invites'], queryFn: () => api.groups.invites(g.id) });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['groups', g.id] });
    qc.invalidateQueries({ queryKey: ['groups'], exact: true });
  };

  const inviteByEmail = useMutation({
    mutationFn: () => api.groups.inviteByEmail(g.id, { email }),
    onSuccess: (res) => {
      refresh();
      setEmail('');
      toast.success(
        res.result === 'added'
          ? 'Hat schon ein Konto: direkt hinzugefügt und per Mail informiert'
          : res.result === 'already_member'
            ? 'Ist bereits Mitglied'
            : 'Einladung per E-Mail verschickt',
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createLink = useMutation({
    mutationFn: () =>
      api.groups.createInvite(g.id, {
        label: label.trim() || null,
        maxUses: usage === 'limited' ? Math.max(1, parseInt(maxUses, 10) || 1) : null,
        expiresInDays: expiresInDays ? Math.max(1, parseInt(expiresInDays, 10) || 1) : null,
      }),
    onSuccess: async (res) => {
      refresh();
      setLabel('');
      const url = inviteLink(res.invite.token);
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success('Link erstellt und kopiert');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (email.trim()) inviteByEmail.mutate();
  }

  function submitLink(e: FormEvent) {
    e.preventDefault();
    createLink.mutate();
  }

  const list = invites.data?.invites ?? [];
  const visible = showRevoked ? list : list.filter((i) => i.active);
  const hiddenCount = list.length - list.filter((i) => i.active).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-text">
            <Mail className="h-4 w-4 text-gold" />
            Manuell einladen
          </div>
          <p className="text-xs text-text-muted">
            Hat die Person schon ein Nexply-Konto, ist sie sofort in der Gruppe. Sonst bekommt sie eine persönliche
            Einladung per E-Mail (einmal nutzbar, 30 Tage gültig).
          </p>
          <form onSubmit={submitEmail} className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="email"
              value={email}
              placeholder="name@firma.de"
              onChange={(e) => setEmail(e.currentTarget.value)}
              className="flex-1"
            />
            <Button type="submit" size="sm" className="sm:h-auto" loading={inviteByEmail.isPending} disabled={!email.trim()}>
              <UserPlus className="h-4 w-4" />
              Einladen
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-text">
            <Link2 className="h-4 w-4 text-gold" />
            Einladungslink erstellen
          </div>
          <form onSubmit={submitLink} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="inv-label">Bezeichnung (optional)</Label>
              <Input
                id="inv-label"
                value={label}
                maxLength={120}
                placeholder="z. B. WhatsApp-Gruppe, Event März"
                onChange={(e) => setLabel(e.currentTarget.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nutzungen</Label>
              <SegmentedControl<UsageMode>
                size="sm"
                value={usage}
                onChange={setUsage}
                options={[
                  { value: 'unlimited', label: 'Dauerhaft' },
                  { value: 'limited', label: 'Begrenzt' },
                ]}
              />
              {usage === 'limited' && (
                <div className="flex items-center gap-2 pt-1">
                  <Input
                    type="number"
                    min={1}
                    max={10000}
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.currentTarget.value)}
                    className="w-28"
                  />
                  <span className="text-sm text-text-dim">mal nutzbar</span>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-exp">Läuft ab nach (Tagen, optional)</Label>
              <Input
                id="inv-exp"
                type="number"
                min={1}
                max={365}
                value={expiresInDays}
                placeholder="leer = kein Ablauf"
                onChange={(e) => setExpiresInDays(e.currentTarget.value)}
                className="w-48"
              />
            </div>
            <Button type="submit" size="sm" loading={createLink.isPending}>
              <Plus className="h-4 w-4" />
              Link erstellen
            </Button>
          </form>
        </CardBody>
      </Card>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-text-muted">
            {g.canManage ? 'Links der Gruppe' : 'Deine Links'}
          </h3>
          {hiddenCount > 0 && (
            <button className="text-xs text-text-dim hover:text-text" onClick={() => setShowRevoked((v) => !v)}>
              {showRevoked ? 'Inaktive ausblenden' : `${hiddenCount} inaktive zeigen`}
            </button>
          )}
        </div>
        {invites.isLoading && <div className="h-16 rounded-xl bg-raise animate-pulse" />}
        {invites.data && visible.length === 0 && (
          <div className="rounded-xl border border-dashed border-line py-8 text-center text-sm text-text-muted">
            Noch keine aktiven Links.
          </div>
        )}
        {visible.map((inv) => (
          <InviteRow key={inv.id} groupId={g.id} inv={inv} />
        ))}
      </div>
    </div>
  );
}

function inviteLink(token: string) {
  return `${window.location.origin}/g/${token}`;
}

function inviteState(inv: GroupInviteDTO): { label: string; tone: 'success' | 'neutral' | 'red' | 'warn' } {
  if (inv.revokedAt) return { label: 'Zurückgezogen', tone: 'red' };
  if (inv.expiresAt && new Date(inv.expiresAt) <= new Date()) return { label: 'Abgelaufen', tone: 'neutral' };
  if (inv.maxUses !== null && inv.uses >= inv.maxUses) return { label: 'Aufgebraucht', tone: 'neutral' };
  return { label: 'Aktiv', tone: 'success' };
}

function InviteRow({ groupId, inv }: { groupId: string; inv: GroupInviteDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const url = inviteLink(inv.token);
  const state = inviteState(inv);

  const revoke = useMutation({
    mutationFn: () => api.groups.revokeInvite(groupId, inv.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups', groupId, 'invites'] });
      toast.success('Link zurückgezogen');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kopieren hat nicht geklappt');
    }
  }

  async function share() {
    if (navigator.share) {
      await navigator.share({ title: 'nexply', text: 'Komm in unsere Netzwerk-Gruppe:', url }).catch(() => {});
      return;
    }
    copy();
  }

  const usageText = inv.maxUses === null ? `${inv.uses} genutzt · unbegrenzt` : `${inv.uses} / ${inv.maxUses} genutzt`;
  const meta = useMemo(
    () =>
      [
        usageText,
        inv.expiresAt ? `bis ${new Date(inv.expiresAt).toLocaleDateString('de-DE')}` : 'ohne Ablauf',
        `von ${inv.createdByName}`,
      ].join(' · '),
    [usageText, inv.expiresAt, inv.createdByName],
  );

  return (
    <div className={cn('rounded-xl border border-line bg-surface-2 p-3', !inv.active && 'opacity-60')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-text">
              {inv.label || (inv.maxUses === null ? 'Dauerhafter Link' : `Link für ${inv.maxUses} Nutzungen`)}
            </span>
            <Badge tone={state.tone}>{state.label}</Badge>
          </div>
          <div className="mt-0.5 text-[11px] text-text-muted">{meta}</div>
          {inv.active && <div className="mt-1.5 break-all text-xs text-text-dim">{url}</div>}
        </div>
        {inv.active && (
          <div className="flex shrink-0 gap-1">
            <IconBtn title="Kopieren" onClick={copy}>
              {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </IconBtn>
            <IconBtn title="QR-Code" onClick={() => setQrOpen(true)}>
              <QrCode className="h-4 w-4" />
            </IconBtn>
            <IconBtn title="Teilen" onClick={share}>
              <Share2 className="h-4 w-4" />
            </IconBtn>
            <IconBtn
              title="Zurückziehen"
              danger
              onClick={() => {
                if (confirm('Link zurückziehen? Wer ihn danach öffnet, kann nicht mehr beitreten.')) revoke.mutate();
              }}
            >
              <Trash2 className="h-4 w-4" />
            </IconBtn>
          </div>
        )}
      </div>
      {inv.active && (
        <QRModal
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          value={url}
          title="QR-Code · Gruppen-Einladung"
          downloadName="nexply-gruppe-qr.png"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Einstellungen (nur Ersteller)
// ---------------------------------------------------------------------------

function SettingsTab({ g }: { g: GroupDetailDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const [name, setName] = useState(g.name);
  const [description, setDescription] = useState(g.description ?? '');
  const [policy, setPolicy] = useState<GroupInvitePolicy>(g.invitePolicy);

  const dirty = name !== g.name || description !== (g.description ?? '') || policy !== g.invitePolicy;

  const save = useMutation({
    mutationFn: () => api.groups.update(g.id, { name, description, invitePolicy: policy }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Gespeichert');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.groups.remove(g.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Gruppe gelöscht');
      nav({ to: '/groups' });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() && dirty) save.mutate();
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardBody>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="gs-name">Name</Label>
              <Input id="gs-name" value={name} maxLength={120} onChange={(e) => setName(e.currentTarget.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gs-desc">Beschreibung</Label>
              <Textarea
                id="gs-desc"
                rows={3}
                maxLength={1000}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Wer darf Leute einladen?</Label>
              <InvitePolicyPicker value={policy} onChange={setPolicy} />
              {policy === 'ADMINS_ONLY' && g.invitePolicy === 'ALL_MEMBERS' && (
                <p className="text-xs text-text-muted">
                  Links, die Mitglieder schon erstellt haben, bleiben gültig. Du kannst sie unter „Einladen" zurückziehen.
                </p>
              )}
            </div>
            <Button type="submit" size="sm" loading={save.isPending} disabled={!dirty || !name.trim()}>
              Speichern
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-danger">
            <Users className="h-4 w-4" />
            Gruppe löschen
          </div>
          <p className="text-xs text-text-muted">
            Löscht die Gruppe mit allen gemeinsamen Kontakten und Einladungslinks. Die persönlichen Netzwerke der Mitglieder
            bleiben unberührt.
          </p>
          <Button
            variant="danger"
            size="sm"
            loading={remove.isPending}
            onClick={() => {
              if (confirm(`„${g.name}" endgültig löschen? Das lässt sich nicht rückgängig machen.`)) remove.mutate();
            }}
          >
            <Trash2 className="h-4 w-4" />
            Endgültig löschen
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
